import { Link } from "react-router-dom";
import { ArrowRight, Clock, FileText, Users, Download } from "lucide-react";
import { SignInButton, SignedOut } from "@clerk/clerk-react";

const DIFFERENTIATORS = [
  {
    icon: FileText,
    title: "Ontario-aligned templates",
    desc: "Built directly from Ministry of Education formats — Progress Reports (Gr 1–6) and Provincial Report Cards (Gr 1–8). Comments match the language evaluators expect.",
  },
  {
    icon: Clock,
    title: "From hours to minutes",
    desc: "Teachers tell us they spend several hours per reporting period on comments alone. EasyReports brings that down to a fraction of the time.",
  },
  {
    icon: Users,
    title: "Class sharing & collaboration",
    desc: "Share a class with a co-teacher or occasional teacher and give them view or edit access — no shared logins required.",
  },
  {
    icon: Download,
    title: "Print-ready PDFs, instantly",
    desc: "Generate completed report card PDFs directly in the browser. No software, no formatting, no copy-pasting. Just download and submit.",
  },
];

export default function About() {
  return (
    <div className="about-page">

      {/* ── Intro ───────────────────────────────────────────────── */}
      <section className="section-base">
        <div className="section-inner about-intro">
          <div className="section-label">About EasyReports</div>
          <h1 className="section-heading about-heading">
            Built for the teachers who do the most with the least time.
          </h1>
          <p className="about-lead">
            Every Ontario elementary teacher spends hours writing individualized
            report card comments each term — while lesson planning, marking, and
            running a classroom. EasyReports was built to give that time back.
          </p>
        </div>
      </section>

      {/* ── Mission ─────────────────────────────────────────────── */}
      <section className="section-alt">
        <div className="section-inner about-mission-grid">
          <div className="about-mission-text">
            <div className="section-label">Our mission</div>
            <h2 className="section-heading" style={{ marginBottom: "1.25rem" }}>
              Educators should spend their energy on students, not paperwork.
            </h2>
            <p className="about-body">
              By combining Ontario-aligned report card templates with AI-generated
              comments, EasyReports lets you describe a student&apos;s strengths and
              next steps in plain language — and instantly get a polished,
              grade-appropriate comment ready to drop into the report card.
            </p>
            <p className="about-body">
              We built this for Grades 1–8 teachers in Ontario who complete Progress
              Reports or Provincial Report Cards each term. Whether you have 20
              students or 30, the platform keeps everything organized in one place
              and handles the export for you.
            </p>
          </div>
          <div className="about-mission-stat-col">
            <div className="about-stat-card">
              <div className="about-stat-number">30+</div>
              <div className="about-stat-label">students per class, every term</div>
            </div>
            <div className="about-stat-card">
              <div className="about-stat-number">~4h</div>
              <div className="about-stat-label">avg. time teachers spend on comments</div>
            </div>
            <div className="about-stat-card">
              <div className="about-stat-number">&lt;5m</div>
              <div className="about-stat-label">with EasyReports</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Differentiators ─────────────────────────────────────── */}
      <section className="section-base">
        <div className="section-inner">
          <div className="section-label">What makes it different</div>
          <h2 className="section-heading">Designed for how Ontario teachers actually work.</h2>
          <div className="features-grid">
            {DIFFERENTIATORS.map((d) => (
              <div key={d.title} className="feature-card">
                <div className="feature-icon">
                  <d.icon size={20} />
                </div>
                <h3 className="feature-title">{d.title}</h3>
                <p className="feature-desc">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────── */}
      <section className="section-dark">
        <div className="section-inner" style={{ textAlign: "center" }}>
          <div className="section-label light">Try it now</div>
          <h2 className="section-heading light">
            See it work on a real student in under two minutes.
          </h2>
          <div className="hero-ctas" style={{ justifyContent: "center" }}>
            <Link to="/pgrc">
              <button className="cta-primary">
                Try a free report card <ArrowRight size={16} />
              </button>
            </Link>
            <SignedOut>
              <SignInButton mode="modal" afterSignInUrl="/dashboard" afterSignUpUrl="/dashboard">
                <button className="cta-secondary light">Create free account</button>
              </SignInButton>
            </SignedOut>
          </div>
        </div>
      </section>

    </div>
  );
}
